import { IconType } from 'react-icons';
import React, { KeyboardEventHandler } from 'react';
import { SnackbarOrigin, VariantType } from 'notistack';
import { Mark } from '@material-ui/core';
import { RegisterOptions } from 'react-hook-form';

export interface Tag {
  created_at?: string;
  has_trades?: boolean;
  order?: number;
  is_apprentice?: boolean;
  verified?: boolean;
  ISO3?: string;
  group?: string;
  uuid: string;
  name: string;
  label?: string;
  updated_at?: string;
}

export enum TagGroups {
  ROLE = 'role',
  QUALIFICATION = 'qualification',
  SKILL = 'skill',
}

export interface FieldProps {
  limit?: number;
  lengthToShowLimitMessage?: number;
  rows?: number;
  name?: string;
  autoFocus?: boolean;
  multiline?: boolean;
  margin?: any;
  label?: string;
  rules?: any;
  multiple?: boolean;
  disabled?: boolean;
  isMobile?: boolean;
  token?: string;
  required?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  maxDate?: number;
  type?: string;
  fullWidth?: boolean;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}

export interface Devices {
  isMobile: boolean;
  isDesktop: boolean;
  isLargeDesktop: boolean;
  isTablet?: boolean;
  iOS: boolean;
}

export interface ColorTheme {
  colorTheme: ColorThemeType;
}

export enum ThemeType {
  OS = 'os',
  DARK = 'dark',
  LIGHT = 'light',
}

export type ColorThemeType = 'light' | 'dark';

export type ColorOption =
  | 'grey'
  | 'secondary'
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'paper'
  | 'default';

export interface Nationality {
  uuid: string;
  name: string;
  iso3: string;
  order: number;
}

export interface Photo {
  uuid?: string;
  title?: string;
  file_name?: string;
  url?: string;
}

export interface Image {
  file: File | null;
  url: string | undefined;
  name?: string;
}

export interface Address {
  uuid?: string;
  // house number
  address_line_1: string;
  // street
  address_line_2?: string;
  // town
  address_line_3?: string;
  // postcode
  address_line_4?: string;
  address_snippet?: string;
  country?: string;
  lon?: number;
  lat?: number;
}

export interface Language {
  uuid?: string;
  name: string;
  order?: number;
}

export interface NavItem {
  name: string;
  icon?: IconType;
  nestedItems?: NavItem[];
  isActive?: boolean;
  permissions?: string[];
  breadcrumb?: string;
  /** In-app route, or an absolute `https://` URL — the latter opens in a new tab. */
  link: string;
  /** "Beta" pill next to the name, for early-stage products. */
  isBeta?: boolean;
  isBottom?: boolean;
  disabled?: boolean;
  /**
   * Drop the item entirely when the person holds none of its `permissions`.
   * The default is to keep it: the screen behind it answers with a holding page,
   * which reads better than an item that silently disappears.
   */
  hideWithoutPermissions?: boolean;
  /** Purple badge on the icon, e.g. unread messages; hidden while zero/undefined. */
  count?: number;
}

export interface AutocompleteProps<T> {
  name: string;
  items?: T[];
  multiple?: boolean;
  label?: string;
  noOptionsText?: string;
  rules?: any;
  disableClearable?: boolean;
  defaultValue?: T | T[] | null;
  margin?: any;
  onChange?: Function;
  disabled?: boolean;
  token?: string;
  group?: string;
  creatable?: boolean;
  isTags?: boolean;
  saveInDatabase?: boolean;
  endAdornment?: React.ReactNode;
  manualMode?: boolean;
  filterSelectedOptions?: boolean;
  height?: string;
  setSubstring?: (arg0: string) => void;
  dropdownContainer?: HTMLElement | null;
  getOptionSelected?: (option: T, value: T) => boolean;
}

export interface TabItem {
  label: string;
  link: string;
  /** Counter badge next to the label, e.g. unread messages; hidden while zero. */
  count?: number;
  component?: React.FC;
  /** Any-of: the tab shows when the user holds at least one of these. */
  permission?: import('./permission').UserPermission | import('./permission').UserPermission[];
}

export interface SelectOption {
  value: string;
  name: string;
}

export type FilterOption = {
  type: 'select' | 'slider' | 'date' | 'input' | 'checkbox' | 'radio' | 'autocomplete' | 'grouped_select';
  dataType?: string;
  multiple?: boolean;
  label: string;
  field: string;
  values?: any;
  defaultValue?: any;
  marks?: Mark[];
  step?: number;
  max?: number;
  min?: number;
  hintMessage?: string;
  rules?: RegisterOptions;
  groupOneOptions?: SelectOption[];
  groupTwoOptions?: SelectOption[];
};

export interface FilterSection {
  title: string;
  filters: FilterOption[];
  expanded?: boolean;
}

export interface RadioDataItem {
  value: string | boolean;
  label: string;
}

export interface OrderByItem {
  field_name: string | number;
  desc: boolean;
}

export interface TimeOrderByItem {
  field: string;
  desc: boolean;
}

export interface AppNotification {
  message: string;
  description?: string;
  text?: string;
  options: {
    action?: {
      buttonText: string;
      type: 'verify_email' | null;
    };
    anchorOrigin?: SnackbarOrigin;
    permanent?: boolean;
    variant: VariantType;
  };
}

export enum Statuses {
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  PENDING = 'PENDING',
  NONE = 'NONE',
}

export interface Stages {
  30: string;
  60: string;
  75: string;
  95: string;
  100: string;
}

export type ProgressOptions = 30 | 60 | 75 | 95 | 100;

export interface Task {
  uuid?: string;
  date_done?: string;
  progress: ProgressOptions;
  message?: string;
  status: string;
  task_name: string;
}

export enum TaskNames {
  CHECK_EUSR = 'check_eusr',
  CHECK_QUALIFICATIONS = 'check_qualifications',
  CHECK_RTW = 'check_rtw',
  CHECK_PASSPORT = 'check_passport',
}

export interface AppCustomTableColumn {
  name: string;
  title: string | React.ReactNode;
  flex: number;
  renderCell?: (row: any) => React.ReactNode;
  width?: string;
  justifyContent?: string;
  hint?: string;
  valueGetter?: (row: any) => React.ReactNode;
}

export interface BackToData {
  text: string;
  back: Function;
}

export interface Breadcrumb {
  name: string;
  route?: string;
  level: number;
}

export interface MapMarker {
  lat?: number;
  lon?: number;
  size?: number;
  picture?: string;
  draggable?: boolean;
  eventHandlers?: any;
  name: string;
  id: string;
}

export interface PieChartDataItem {
  color?: string;
  percent?: string;
  id: any;
  label: any;
  value: string | number;
}

export interface HowItWorksStep {
  label: string;
  subtitle: string;
  imgPath: any;
  link?: string;
}

export interface MappingItem {
  value: string;
  label: string;
  matchedValue?: string;
  required: boolean;
}

export interface DetailItem {
  title?: string;
  value?: string;
  verified?: boolean;
}

export interface S3FileData {
  uuid: string;
  s3_key_small: string;
  s3_key_large: string;
}

export interface ChangeLog {
  actor: {
    type: string;
    id: string;
    metadata: Record<string, unknown>;
  };
  changes: {
    name: string;
    description: string | null;
    old_value: string | null;
    new_value: string | null;
  }[];
  updated_at: string;
}
